import { useEffect } from 'react';

import { DownloadMobileAppModal } from '@/components/modals/DownloadMobileAppModal';
import { GoogleAccessibilityModal } from '@/components/modals/GoogleAccessibilityModal';
import { ModalsManager, type ModalFC } from '@/components/modals/modalsManager';
import { VsCodeForkAccessibilityModal } from '@/components/modals/VsCodeForkAccessibilityModal';
import { ChatPromptLibraryModal } from '@/features/chat/chat-prompt-library/chat-prompt-library.modals';
import { DataExportRequestModal } from '@/features/data-export-request/data-export-request';
import { FeedbackModal } from '@/features/feedback/feedback';
import { GrowthModal } from '@/features/growth';
import { ReferralModal } from '@/features/referral';
import { StudentDiscountRequestModal } from '@/features/student-discount-request/student-discount-request';
import { useHashRoute } from '@/hooks/useHashRoute';

type ModalConfig = {
  hash: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- each entry opens a modal with its own props
  modal: ModalFC<any>;
  props?: Record<string, unknown>;
};

const MODAL_HASH_CONFIG: ModalConfig[] = [
  {
    hash: 'feedback',
    modal: FeedbackModal,
  },
  {
    hash: 'invite',
    modal: ReferralModal,
  },
  {
    hash: 'ai-wrapped',
    modal: GrowthModal,
  },
  {
    hash: 'download-mobile-app',
    modal: DownloadMobileAppModal,
  },
  {
    hash: 'chat-library',
    modal: ChatPromptLibraryModal,
  },
  {
    hash: 'google-accessibility',
    modal: GoogleAccessibilityModal,
  },
  {
    hash: 'vscode-accessibility',
    modal: VsCodeForkAccessibilityModal,
    props: { app: 'vscode' },
  },
  {
    hash: 'cursor-accessibility',
    modal: VsCodeForkAccessibilityModal,
    props: { app: 'cursor' },
  },
  {
    hash: 'antigravity-accessibility',
    modal: VsCodeForkAccessibilityModal,
    props: { app: 'antigravity' },
  },
  {
    hash: 'windsurf-accessibility',
    modal: VsCodeForkAccessibilityModal,
    props: { app: 'windsurf' },
  },
  {
    hash: 'data-export-request',
    modal: DataExportRequestModal,
  },
  {
    hash: 'student-discount-request',
    modal: StudentDiscountRequestModal,
  },
];

export function useModalHash() {
  const { hash, clearHash } = useHashRoute();

  useEffect(() => {
    // Strip tracking noise (e.g. GA _gl params) — the canonical hash is everything before the first & or ?
    const cleanHash = hash.split(/[&?]/)[0];

    const matchedConfig = MODAL_HASH_CONFIG.find(
      (config) => config.hash === cleanHash
    );

    if (matchedConfig) {
      ModalsManager.show(
        matchedConfig.modal,
        {
          ...matchedConfig.props,
          onOpenChange: (open: boolean) => {
            if (!open) {
              clearHash();
            }
          },
        },
        { unique: true }
      );
    } else {
      MODAL_HASH_CONFIG.forEach((config) => {
        ModalsManager.hide(config.modal);
      });
    }
  }, [hash, clearHash]);
}
